import React, { useState, useEffect, useRef, useContext } from 'react';
import { IoIosInformationCircleOutline } from "react-icons/io";
import { formatAmountWithCurrency } from '../../utils/stringUtils';
import { YearContext } from '../../context/context';
import Popup from '../Elements/Popup';
import en from '../../copy/en';
import { useToast } from '../../context/ToastContext';

export default function BudgetAllocationTable({
  monthData,
  currency,
  selectedMonth,
  onSave
}) {
  const { selectedYear } = useContext(YearContext);
  const { showToast } = useToast();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isGoalTooltipOpen, setIsGoalTooltipOpen] = useState(false);
  const [isActualTooltipOpen, setIsActualTooltipOpen] = useState(false);
  const [goals, setGoals] = useState({
    needs: 50,
    savings: 20,
    wants: 30
  });

  // Track the current month/year to detect when we switch months
  const currentMonthRef = useRef(null);

  // Calculate totals from monthData - always calculate from current data, don't trust stored totals
  const totalIncome = monthData?.income ? monthData.income.reduce((sum, item) => sum + Number(item.amount || 0), 0) : 0;
  
  const needsAmount = monthData?.needs ? monthData.needs.reduce((sum, item) => sum + Number(item.amount || 0), 0) : 0;
  
  const savingsAmount = monthData?.savings ? monthData.savings.reduce((sum, item) => sum + Number(item.amount || 0), 0) : 0;
  
  const wantsAmount = monthData?.wants ? monthData.wants.reduce((sum, item) => sum + Number(item.amount || 0), 0) : 0;

  // Load goals from monthData only when switching to a different month
  useEffect(() => {
    if (monthData && monthData.budgetGoals) {
      const currentMonthKey = `${monthData.year}-${monthData.month}`;
      
      // Only update goals if we're switching to a different month
      if (currentMonthRef.current !== currentMonthKey) {
        currentMonthRef.current = currentMonthKey;
        
        // Map backend field names to frontend field names
        setGoals({
          needs: monthData.budgetGoals.needsPercentage || 50,
          savings: monthData.budgetGoals.savingsPercentage || 20,
          wants: monthData.budgetGoals.wantsPercentage || 30
        });
      }
    }
  }, [monthData]);

  // Calculate percentages
  const needsPercentage = totalIncome > 0 ? Math.round((needsAmount / totalIncome) * 100) : 0;
  const savingsPercentage = totalIncome > 0 ? Math.round((savingsAmount / totalIncome) * 100) : 0;
  const wantsPercentage = totalIncome > 0 ? Math.round((wantsAmount / totalIncome) * 100) : 0;
  
  // Calculate ideal amounts based on editable goals
  const idealNeeds = totalIncome * (goals.needs / 100);
  const idealSavings = totalIncome * (goals.savings / 100);
  const idealWants = totalIncome * (goals.wants / 100);
  
  // Calculate total spent and remaining
  const totalSpent = needsAmount + savingsAmount + wantsAmount;
  const remainingAmount = totalIncome - totalSpent;

  const updateGoal = (category, value) => {
    // Allow only numbers and decimal point
    const numericValue = value.replace(/[^0-9.]/g, '');
    // Prevent multiple decimal points
    const parts = numericValue.split('.');
    const cleanValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numericValue;
    let newValue = Math.max(0, Math.min(100, parseFloat(cleanValue) || 0));

    // Calculate the sum of the other two categories
    const otherCategories = Object.keys(goals).filter(key => key !== category);
    const otherCategoriesSum = otherCategories.reduce((sum, key) => sum + goals[key], 0);

    // Ensure the total doesn't exceed 100%
    const maxAllowedValue = 100 - otherCategoriesSum;
    newValue = Math.min(newValue, maxAllowedValue);

    const newGoals = {
      ...goals,
      [category]: newValue
    };
    setGoals(newGoals);

    // Save goals to backend with proper field mapping
    if (onSave) {
      const budgetGoals = {
        needsPercentage: newGoals.needs,
        savingsPercentage: newGoals.savings,
        wantsPercentage: newGoals.wants
      };
      onSave({ budgetGoals });
      showToast('Budget goal updated successfully', 'success');
    }
  };

  const tooltipContent = en.finance.tooltips.budget_allocation.description;

  const goalTooltipContent = en.finance.tooltips.goal_percentages.description;

  const actualTooltipContent = en.finance.tooltips.actual_percentages.description;

  return (
    <div className="w-full h-full card-base-border card-base card-base-background overflow-hidden">
      {/* Header - simplified without controls */}
      <div className="p-3 card-base-border-bottom">
        <div className="flex items-center gap-2">
          <h3 className="text-xl mb-4">Budget {selectedMonth} {selectedYear}</h3>
          <button
            onClick={() => setIsPopupOpen(true)}
            className="cursor-pointer"
          >
            <IoIosInformationCircleOutline className="transition-colors" size={16} />
          </button>
        </div>
      </div>
      
      {/* Table */}
      <div className="overflow-x-auto overflow-y-hidden">
        <div className="min-w-[600px]">
          {/* Table Header */}
          <div className="grid grid-cols-6 gap-2 p-1.5 card-base-border-bottom background-page-color">
            <div className="col-span-2 text-sm font-bold">Category</div>
            <div className=" text-sm text-center font-bold">Ideal amount</div>
            <div className=" text-sm text-center flex items-center justify-center gap-1 font-bold">
              Goal
              <button
                onClick={() => setIsGoalTooltipOpen(true)}
                className="cursor-pointer"
              >
                <IoIosInformationCircleOutline className="transition-colors" size={14} />
              </button>
            </div>
            <div className=" text-sm text-center flex items-center justify-center gap-1 font-bold">
              Actual
              <button
                onClick={() => setIsActualTooltipOpen(true)}
                className="cursor-pointer"
              >
                <IoIosInformationCircleOutline className="transition-colors" size={14} />
              </button>
            </div>
            <div className=" text-sm text-center font-bold">Total spent</div>
          </div>
          
          {/* Needs Row */}
          <div className="grid grid-cols-6 gap-2 p-3 card-base-border-bottom">
            <div className="col-span-2">Needs</div>
            <div className="text-center whitespace-nowrap">{formatAmountWithCurrency(idealNeeds, currency)}</div>
            <div className="text-center">
              <input
                type="text"
                min="0"
                max="100"
                value={goals.needs}
                onChange={(e) => updateGoal('needs', e.target.value)}
                className="w-12 card-base-border rounded px-1 py-1 text-center text-sm focus:outline-none focus:border-blue-500"
                placeholder="50"
              />%
            </div>
            <div className={`text-center ${needsPercentage > goals.needs ? 'text-red-500' : ''}`}>
              {needsPercentage}%
            </div>
            <div className="text-center whitespace-nowrap">{formatAmountWithCurrency(needsAmount, currency)}</div>
          </div>
          
          {/* Savings Row */}
          <div className="grid grid-cols-6 gap-2 p-3 card-base-border-bottom">
            <div className="col-span-2">Savings & Investments</div>
            <div className="text-center whitespace-nowrap">{formatAmountWithCurrency(idealSavings, currency)}</div>
            <div className="text-center">
              <input
                type="text"
                min="0"
                max="100"
                value={goals.savings}
                onChange={(e) => updateGoal('savings', e.target.value)}
                className="w-12 card-base-border rounded px-1 py-1 text-center text-sm focus:outline-none focus:border-blue-500"
                placeholder="20"
              />%
            </div>
            <div className={`text-center ${savingsPercentage > goals.savings ? 'text-red-500' : ''}`}>
              {savingsPercentage}%
            </div>
            <div className="text-center whitespace-nowrap">{formatAmountWithCurrency(savingsAmount, currency)}</div>
          </div>
          
          {/* Wants Row */}
          <div className="grid grid-cols-6 gap-2 p-3 card-base-border-bottom">
            <div className="col-span-2">Wants</div>
            <div className="text-center whitespace-nowrap">{formatAmountWithCurrency(idealWants, currency)}</div>
            <div className="text-center">
              <input
                type="text"
                min="0"
                max="100"
                value={goals.wants}
                onChange={(e) => updateGoal('wants', e.target.value)}
                className="w-12 card-base-border rounded px-1 py-1 text-center text-sm focus:outline-none focus:border-blue-500"
                placeholder="30"
              />%
            </div>
            <div className={`text-center ${wantsPercentage > goals.wants ? 'text-red-500' : ''}`}>
              {wantsPercentage}%
            </div>
            <div className="text-center whitespace-nowrap">{formatAmountWithCurrency(wantsAmount, currency)}</div>
          </div>
          
          {/* Total Spent Row */}
          <div className="grid grid-cols-6 gap-2 p-3 card-base-border-bottom background-page-color">
            <div className="col-span-2 font-bold">Total spend</div>
            <div></div>
            <div></div>
            <div></div>
            <div className="text-center font-bold whitespace-nowrap">{formatAmountWithCurrency(totalSpent, currency)}</div>
          </div>
          
          {/* Remaining Amount Row */}
          <div className={`grid grid-cols-6 gap-2 p-3 ${
            remainingAmount === 0 && totalIncome === 0 ? 'background-page-color' :
            remainingAmount > 0 ? 'bg-accent-light' :
            remainingAmount < 0 ? 'bg-red-100' :
            'bg-green-100'
          }`}>
            <div className="col-span-5">
              {remainingAmount > 0 ? 'You haven\'t allocated all of your income, the amount left is' :
               remainingAmount < 0 ? 'You have overspent your income by:' :
               'Your income is perfectly allocated!'}
            </div>
            <div className={`text-center font-bold whitespace-nowrap ${
              remainingAmount === 0 && totalIncome === 0 ? '' :
              remainingAmount > 0 ? 'text-orange-600' :
              remainingAmount < 0 ? 'text-red-600' :
              'text-green-600'
            }`}>
              {formatAmountWithCurrency(Math.abs(remainingAmount), currency)}
            </div>
          </div>
        </div>
      </div>

      {/* Popup */}
      <Popup
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        title="Budget Allocation Overview"
        buttonPrimaryCTA="Close"
        onClickPrimaryLink={() => setIsPopupOpen(false)}
      >
        <div className="text-sm whitespace-pre-line">
          {tooltipContent}
        </div>
      </Popup>

      {/* Actual Tooltip Popup */}
      <Popup
        isOpen={isActualTooltipOpen}
        onClose={() => setIsActualTooltipOpen(false)}
        title="Actual Percentages"
        buttonPrimaryCTA="Close"
        onClickPrimaryLink={() => setIsActualTooltipOpen(false)}
      >
        <div className="text-sm whitespace-pre-line">
          {actualTooltipContent}
        </div>
      </Popup>

      {/* Goal Tooltip Popup */}
      <Popup
        isOpen={isGoalTooltipOpen}
        onClose={() => setIsGoalTooltipOpen(false)}
        title="Goal Percentages"
        buttonPrimaryCTA="Close"
        onClickPrimaryLink={() => setIsGoalTooltipOpen(false)}
      >
        <div className="text-sm whitespace-pre-line">
          {goalTooltipContent}
        </div>
      </Popup>

      {/* Actual Tooltip Popup */}
      <Popup
        isOpen={isActualTooltipOpen}
        onClose={() => setIsActualTooltipOpen(false)}
        title="Actual Percentages"
        buttonPrimaryCTA="Close"
        onClickPrimaryLink={() => setIsActualTooltipOpen(false)}
      >
        <div className="text-sm whitespace-pre-line">
          {actualTooltipContent}
        </div>
      </Popup>
    </div>
  );
}
